import { parseHost, stripHostArtifacts } from '@/Domain/helper/scrapeUrlClassifier';
import {
  APP_ATLASSIAN,
  APP_LINEAR,
  APP_NOTION,
  APP_OTHER_API_ID,
  CHANNEL_DISCORD,
  CHANNEL_INSTAGRAM,
  CHANNEL_MESSENGER,
  CHANNEL_TELEGRAM,
  CHANNEL_WHATSAPP,
  ONBOARDING_APP_TO_MCP_CATALOG_NAME,
} from '@/UI/GettingStarted/components/onboardingOptions';

// How we CAN reach a host's content once scraping it has proven impossible: `channel` means a
// native integration, `mcp` a hosted server already in MCP_CATALOG, `api` a custom HTTP action.
export type SaasRemedyKind = 'channel' | 'mcp' | 'api';

export type SaasRemedy = {
  kind: SaasRemedyKind;
  name: string;
  // Doubles as the id written into the onboarding selection and as the `?open=` deep-link id the
  // post-onboarding checklist emits: /channels for `channel`, /actions for `mcp` and `api`.
  openId: string;
  // `api` only: a docs page walking through the custom action.
  docsUrl?: string;
};

export const JIRA_DOCS_URL = 'https://docs.quickchat.ai/channels/jira';
// Confluence has no dedicated page yet, so it gets the general Actions guide rather than being
// handed Jira's. Swap this for `/channels/confluence` once that page exists.
export const ACTIONS_DOCS_URL = 'https://docs.quickchat.ai/ai-agent/actions';

// Maps, not object literals, throughout this file: every lookup below is keyed by a label taken
// straight out of a URL the user typed, and a plain object answers `constructor` / `__proto__`
// off the prototype — which would hand back a junk remedy with an `undefined` openId. Same
// reasoning as `ONBOARDING_APP_TO_MCP_CATALOG_NAME`.
const CHANNEL_HOSTS = new Map<string, { name: string; openId: string }>([
  ['discord.com', { name: 'Discord', openId: CHANNEL_DISCORD }],
  ['discord.gg', { name: 'Discord', openId: CHANNEL_DISCORD }],
  ['discordapp.com', { name: 'Discord', openId: CHANNEL_DISCORD }],
  ['instagram.com', { name: 'Instagram', openId: CHANNEL_INSTAGRAM }],
  ['facebook.com', { name: 'Messenger', openId: CHANNEL_MESSENGER }],
  ['messenger.com', { name: 'Messenger', openId: CHANNEL_MESSENGER }],
  ['telegram.org', { name: 'Telegram', openId: CHANNEL_TELEGRAM }],
  ['telegram.me', { name: 'Telegram', openId: CHANNEL_TELEGRAM }],
  ['t.me', { name: 'Telegram', openId: CHANNEL_TELEGRAM }],
  ['whatsapp.com', { name: 'WhatsApp', openId: CHANNEL_WHATSAPP }],
  ['wa.me', { name: 'WhatsApp', openId: CHANNEL_WHATSAPP }],
  // `m.me/SomeOne` is Messenger's short link, the pair of `wa.me` above — `scrapeUrlClassifier`
  // already treats the two as one family in `UNSUPPORTED_SITE_HOSTS`.
  ['m.me', { name: 'Messenger', openId: CHANNEL_MESSENGER }],
]);

// Subdomains of the channel hosts above that serve documentation or corporate marketing rather
// than the consumer product: `developers.facebook.com` is a docs site, not a sign that the user
// wants Messenger. Everything else under those apexes still matches, because the hosts users
// actually paste are spread across arbitrary subdomains (`cdn.discordapp.com`,
// `core.telegram.org`, `chat.whatsapp.com` were all submitted in production).
//
// A guard rather than a hot path: these subdomains serve public HTML and scrape fine, so they do
// not normally reach a classifier that only ever runs on a host we failed to read.
const NON_PRODUCT_SUBDOMAINS = new Set([
  'about',
  'business',
  'careers',
  'developer',
  'developers',
  'engineering',
  'investors',
  'newsroom',
  'research',
]);

const MCP_HOSTS = new Map<string, string>([
  // Atlassian Cloud tenants (`acme.atlassian.net`) plus the shared sign-in wall the same
  // customers land on (`id.atlassian.com`) — both are served by mcp.atlassian.com.
  ['atlassian.net', APP_ATLASSIAN],
  ['atlassian.com', APP_ATLASSIAN],
  ['notion.so', APP_NOTION],
  ['notion.site', APP_NOTION],
  ['notion.com', APP_NOTION],
  ['linear.app', APP_LINEAR],
]);

// mcp.atlassian.com serves Atlassian Cloud only, so a self-hosted Jira or Confluence has to go
// through its own REST API instead. Matched on a SUBDOMAIN label — everything ahead of the
// registrable suffix — which covers both shapes production has actually shown us
// (`jira.acme.com` and `easydelivery.jira.capgemini.com`, the case that prompted this) without
// claiming a company whose own domain is named for the product (`jira.com`,
// `confluence-interiors.com`).
const SELF_HOSTED_ATLASSIAN = new Map<string, { name: string; docsUrl: string }>([
  ['jira', { name: 'Jira', docsUrl: JIRA_DOCS_URL }],
  ['confluence', { name: 'Confluence', docsUrl: ACTIONS_DOCS_URL }],
]);

const apiRemedy = (name: string, docsUrl: string): SaasRemedy => ({
  kind: 'api',
  name,
  openId: APP_OTHER_API_ID,
  docsUrl,
});

// Never promise a server the catalog does not carry: without a catalog name the deep link would
// open an empty connect form, so fall back to the custom-action route.
const mcpRemedy = (openId: string): SaasRemedy => {
  const name = ONBOARDING_APP_TO_MCP_CATALOG_NAME.get(openId);
  if (!name) return apiRemedy(openId, ACTIONS_DOCS_URL);
  return { kind: 'mcp', name, openId };
};

const registrableSuffix = (host: string): string => host.split('.').slice(-2).join('.');

// Everything ahead of the registrable suffix, e.g. `www` for `www.discord.com`, `developers` for
// `developers.facebook.com`, and '' for a bare apex.
const subdomainOf = (host: string): string => host.split('.').slice(0, -2).join('.');

export function classifySaasHost(rawHost: string | undefined | null): SaasRemedy | undefined {
  if (!rawHost) return undefined;
  const host = parseHost(stripHostArtifacts(rawHost));
  if (!host) return undefined;

  const apex = registrableSuffix(host);
  const subdomain = subdomainOf(host);

  const channel = CHANNEL_HOSTS.get(apex);
  if (channel && !NON_PRODUCT_SUBDOMAINS.has(subdomain)) {
    return { kind: 'channel', name: channel.name, openId: channel.openId };
  }

  const mcpAppId = MCP_HOSTS.get(apex);
  if (mcpAppId) return mcpRemedy(mcpAppId);

  const selfHosted = subdomain
    .split('.')
    .map((label) => SELF_HOSTED_ATLASSIAN.get(label))
    .find(Boolean);
  if (selfHosted) return apiRemedy(selfHosted.name, selfHosted.docsUrl);

  return undefined;
}
